// LAS MATRÍCULAS PAGADAS. Y la mitad no se ven en la línea del pedido.
//
// Una matrícula es un producto: «Matricula anual VERTEX», 20 €,
// `fulfillment_type = 'membership'`. Se paga por DOS caminos, igual que las
// camisetas del inventario:
//
//   · **línea propia** del pedido, con `fulfillment_type = 'membership'`.
//   · **dentro de un pack**, en `components.items[]`. La línea del pack NO se
//     llama matrícula: se llama «Pack Alta + 3 meses grupal». Hay que bajar.
//
// Medido en producción el 4-oct-2026, con los estados cobrados de
// `lib/ingresos.ts`: **19 matrículas**, 11 de línea propia y **8 dentro del
// pack**. Casi la mitad. Contando solo las líneas propias se perdería el 42 %.
//
// TRES PACKS del catálogo la llevan dentro —«Pack Alta + 3 meses grupal»,
// «Pack Alta sesiones grupales» y «Pack Bienvenida»— aunque hoy solo se haya
// vendido el primero. Por eso la regla NO lleva el id del producto a mano:
// cada componente de la línea trae su propio `fulfillment_type`, congelado en
// el pedido tal como estaba el día de la compra, y eso es lo que se mira. Un
// pack nuevo con matrícula dentro entra solo.
//
// EL ESTADO: la misma lista blanca que los ingresos (`paid · shipped ·
// delivered`). Con solo `'paid'` salían 17 y no 19: las otras dos son
// `delivered`, y una de ellas es un canje a 0 € que nació así. Quedan fuera 9
// líneas `expired` y 2 `cancelled`, que no son matrículas pagadas.
//
// LA FECHA: `orders.created_at`. `profiles.membership_active_until` NO sirve
// para fechar — los 18 perfiles que la tienen comparten `2027-07-31`, que es
// el fin de temporada: dice cuándo caduca, no cuándo se pagó.

/** El `fulfillment_type` que hace de un producto una matrícula. */
export const FULFILLMENT_MATRICULA = 'membership';

export interface ComponenteDeLinea {
  fulfillment_type?: string | null;
  quantity?: number | null;
}

/**
 * Una línea de `orders.items`. Los packs de la v2 traen
 * `components = { v: 2, items: [...] }`; los de videoanálisis, más viejos,
 * traen el ARRAY pelado `[{ type: 'service', … }]`. Las dos formas conviven en
 * el catálogo, así que las dos se aceptan aquí: las viejas no tienen
 * `fulfillment_type` en sus elementos, así que suman cero sin romper nada.
 */
export interface LineaDePedido {
  fulfillment_type?: string | null;
  quantity?: number | null;
  components?: { items?: ComponenteDeLinea[] | null } | ComponenteDeLinea[] | null;
}

export interface PedidoConLineas {
  status: string;
  created_at: string;
  items: LineaDePedido[] | null;
}

export interface PuntoDeMatriculas {
  mes: string;
  delMes: number;
  acumulado: number;
}

/** Unidades, con el 1 por defecto: una línea sin `quantity` es una unidad. */
function unidades(q: number | null | undefined): number {
  return typeof q === 'number' && Number.isFinite(q) && q > 0 ? q : 1;
}

/** Los componentes de una línea, en cualquiera de las dos formas. */
function componentes(l: LineaDePedido): ComponenteDeLinea[] {
  const c = l.components;
  if (c == null) return [];
  if (Array.isArray(c)) return c;
  return c.items ?? [];
}

/**
 * Cuántas matrículas hay en una línea.
 *
 * Si la línea YA es una matrícula, se cuenta ella y no se baja a sus
 * componentes: sumar las dos cosas sería contar la misma matrícula dos veces.
 * Hoy no hay ninguna línea que sea las dos cosas a la vez (medido: cero), pero
 * el orden importa si algún día la hay.
 *
 * Dentro de un pack, las unidades se MULTIPLICAN: dos packs con una matrícula
 * cada uno son dos matrículas. Hoy ambas cantidades son 1 en producción, así
 * que esto no cambia ninguna cifra actual — y por eso mismo un fallo aquí no
 * se vería hasta que alguien comprase dos packs de golpe.
 */
export function matriculasDeLaLinea(l: LineaDePedido): number {
  const n = unidades(l.quantity);
  if (l.fulfillment_type === FULFILLMENT_MATRICULA) return n;
  return componentes(l)
    .filter((c) => c.fulfillment_type === FULFILLMENT_MATRICULA)
    .reduce((suma, c) => suma + n * unidades(c.quantity), 0);
}

export function matriculasDelPedido(p: PedidoConLineas): number {
  return (p.items ?? []).reduce((suma, l) => suma + matriculasDeLaLinea(l), 0);
}

/**
 * La serie del gráfico: por cada mes pedido, las matrículas de ese mes y el
 * acumulado.
 *
 * `meses` son los primeros instantes de cada mes, en orden. El acumulado
 * arranca contando TODO lo anterior al primer mes de la ventana, no en cero:
 * un acumulado que empieza de cero cada vez que se mueve la ventana no es un
 * acumulado, es otro «del mes» con más pasos.
 */
export function serieDeMatriculas(
  pedidos: readonly PedidoConLineas[],
  meses: readonly Date[],
  etiqueta: (m: Date) => string
): PuntoDeMatriculas[] {
  if (meses.length === 0) return [];
  const conFecha = pedidos.map((p) => ({ t: new Date(p.created_at).getTime(), n: matriculasDelPedido(p) }));

  let acumulado = conFecha
    .filter((p) => p.t < meses[0].getTime())
    .reduce((suma, p) => suma + p.n, 0);

  return meses.map((m, i) => {
    // Intervalo medio abierto `[mes, siguiente)`. El último mes no tiene
    // siguiente en la lista, así que su cierre es el infinito: lo de hoy
    // cuenta en el mes en curso.
    const desde = m.getTime();
    const hasta = i + 1 < meses.length ? meses[i + 1].getTime() : Infinity;
    const delMes = conFecha
      .filter((p) => p.t >= desde && p.t < hasta)
      .reduce((suma, p) => suma + p.n, 0);
    acumulado += delMes;
    return { mes: etiqueta(m), delMes, acumulado };
  });
}
